/**
 * Admin → Templates (SPEC §8.1, gap #4).
 *
 * Org-scoped canned reply / internal-note bodies for the composer.
 * Insertion happens via the composer's "Templates" dropdown (see
 * Composer.tsx); this surface is the editor.
 *
 * v1 scope: name, scope (reply | note), optional subject, body. No
 * variable interpolation — body is inserted verbatim into the
 * composer textarea, staff edits before sending.
 */

import { Pencil, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import type { EmailTemplate, EmailTemplateScope } from '@beacon/core';

import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { Textarea } from '@/components/ui/textarea.js';
import {
  useCreateEmailTemplate,
  useDeleteEmailTemplate,
  useEmailTemplates,
  useUpdateEmailTemplate,
} from '@/hooks/use-email-templates.js';

export function AdminTemplatesList() {
  const templatesQuery = useEmailTemplates();
  const [editing, setEditing] = useState<EmailTemplate | 'new' | null>(null);

  if (templatesQuery.isLoading) {
    return <AdminTemplatesListSkeleton />;
  }
  if (templatesQuery.isError) {
    return (
      <div className="p-8 text-ink-300">Couldn't load templates.</div>
    );
  }
  const templates = templatesQuery.data ?? [];
  const replies = templates.filter((t) => t.scope === 'reply');
  const notes = templates.filter((t) => t.scope === 'note');

  return (
    <div className="h-full overflow-y-auto bg-ink-900">
      <header className="px-6 pt-5 pb-4 border-b border-ink-600 bg-ink-850 sticky top-0 z-10 flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-[12px] mono text-ink-300 uppercase tracking-[0.06em]">
            Settings
          </div>
          <h1 className="text-[19px] font-semibold text-ink-100 mt-0.5">
            Templates
          </h1>
          <p className="text-[12.5px] text-ink-300 mt-1.5">
            Canned bodies the composer's <strong>Templates</strong>{' '}
            dropdown can insert. Reply templates surface in client-facing
            replies; note templates in internal notes.
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setEditing('new')}
          data-testid="tpl-new"
        >
          <Plus size={13} />
          New template
        </Button>
      </header>

      <div className="p-6 max-w-3xl space-y-6">
        <Section
          title="Reply templates"
          description="Inserted into the public reply composer. Optional Subject pre-fills on a fresh reply."
          items={replies}
          onEdit={setEditing}
        />
        <Section
          title="Internal-note templates"
          description="Inserted into the internal-note composer. Subject is ignored."
          items={notes}
          onEdit={setEditing}
        />
      </div>

      {editing && (
        <TemplateEditorDialog
          template={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

interface SectionProps {
  title: string;
  description: string;
  items: EmailTemplate[];
  onEdit: (t: EmailTemplate) => void;
}

function Section({ title, description, items, onEdit }: SectionProps) {
  return (
    <section>
      <div className="mb-2.5">
        <h2 className="text-[13.5px] font-semibold text-ink-100">{title}</h2>
        <p className="text-[12px] text-ink-300 mt-0.5">{description}</p>
      </div>
      {items.length === 0 ? (
        <div className="rounded-md border border-ink-600 border-dashed bg-ink-850 p-6 text-center text-[12.5px] text-ink-300">
          None yet.
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((t) => (
            <li
              key={t.id}
              className="rounded-md border border-ink-600 bg-ink-850 px-4 py-3 flex items-start gap-3"
              data-testid={`tpl-row-${t.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[14px] font-medium text-ink-100 truncate">
                    {t.name}
                  </span>
                  <Badge tone={t.scope === 'reply' ? 'blue' : 'yellow'}>
                    {t.scope === 'reply' ? 'Reply' : 'Note'}
                  </Badge>
                </div>
                {t.subject && (
                  <div className="text-[12px] text-ink-300 mb-1 truncate">
                    Subject: <span className="text-ink-200">{t.subject}</span>
                  </div>
                )}
                <p className="text-[12.5px] text-ink-300 line-clamp-2 whitespace-pre-wrap">
                  {t.body}
                </p>
              </div>
              <button
                onClick={() => onEdit(t)}
                className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[12.5px] focus-ring border bg-ink-800 border-ink-600 text-ink-200 hover:text-ink-100"
                aria-label={`Edit ${t.name}`}
              >
                <Pencil size={12} />
                Edit
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

interface EditorProps {
  /** null = create mode; EmailTemplate = edit mode. */
  template: EmailTemplate | null;
  onClose: () => void;
}

function TemplateEditorDialog({ template, onClose }: EditorProps) {
  const create = useCreateEmailTemplate();
  const update = useUpdateEmailTemplate();
  const remove = useDeleteEmailTemplate();
  const isEdit = template !== null;

  const [name, setName] = useState(template?.name ?? '');
  const [scope, setScope] = useState<EmailTemplateScope>(
    template?.scope ?? 'reply'
  );
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [body, setBody] = useState(template?.body ?? '');

  const isPending = create.isPending || update.isPending || remove.isPending;

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !isPending) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isPending, onClose]);

  function handleSubmit() {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!body.trim()) {
      toast.error('Body is required');
      return;
    }

    const payload = {
      name: name.trim(),
      scope,
      subject: scope === 'note' ? null : subject.trim() || null,
      body: body.trim(),
    };

    if (isEdit && template) {
      update.mutate(
        { id: template.id, input: payload },
        { onSuccess: onClose }
      );
    } else {
      create.mutate(payload, { onSuccess: onClose });
    }
  }

  function handleDelete() {
    if (!template) return;
    if (!window.confirm(`Delete "${template.name}"? This can't be undone.`)) {
      return;
    }
    remove.mutate(template.id, { onSuccess: onClose });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dialog-in-backdrop"
      onClick={isPending ? undefined : onClose}
    >
      <div
        className="w-full max-w-xl mx-4 rounded-lg border border-ink-600 bg-ink-850 shadow-xl max-h-[92vh] overflow-y-auto dialog-in-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tpl-editor-title"
      >
        <header className="flex items-start justify-between px-5 pt-4 pb-3 border-b border-ink-700">
          <div>
            <h2
              id="tpl-editor-title"
              className="text-[15px] font-semibold text-ink-100"
            >
              {isEdit ? 'Edit template' : 'New template'}
            </h2>
            <p className="text-[12.5px] text-ink-300 mt-0.5">
              Saved per-organisation. Inserts verbatim into the composer
              — no variables in v1.
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isPending}
            className="text-ink-300 hover:text-ink-100 focus-ring rounded-md p-1 disabled:opacity-50"
            aria-label="Close dialog"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="px-5 py-4 space-y-4">
          <Field label="Name *">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Acknowledge & investigating"
              disabled={isPending}
              data-testid="tpl-name"
              autoFocus
            />
          </Field>

          <Field label="Where it shows">
            <div className="inline-flex rounded-md border border-ink-600 overflow-hidden">
              <ScopeButton
                active={scope === 'reply'}
                onClick={() => setScope('reply')}
                disabled={isPending}
              >
                Reply
              </ScopeButton>
              <ScopeButton
                active={scope === 'note'}
                onClick={() => setScope('note')}
                disabled={isPending}
              >
                Internal note
              </ScopeButton>
            </div>
          </Field>

          {scope === 'reply' && (
            <Field
              label="Subject (optional)"
              hint="Pre-fills Subject on a fresh reply. Leave empty to keep the existing subject."
            >
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. Re: We've seen this — investigating"
                disabled={isPending}
                data-testid="tpl-subject"
              />
            </Field>
          )}

          <Field label="Body *">
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              placeholder="Hi {NAME},&#10;&#10;Thanks for getting in touch — we've seen your message and are investigating.&#10;&#10;— Conduct HQ"
              disabled={isPending}
              data-testid="tpl-body"
            />
            <p className="text-[11.5px] text-ink-400 mt-1">
              Variable interpolation is a v2 feature; for now placeholders
              are typed manually.
            </p>
          </Field>
        </div>

        <footer className="flex items-center justify-between gap-2 px-5 py-3 border-t border-ink-700 bg-ink-900/40">
          <div>
            {isEdit && (
              <button
                onClick={handleDelete}
                disabled={isPending}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[12.5px] focus-ring border bg-ink-800 border-danger-500/40 text-danger-400 hover:text-danger-300 hover:bg-danger-500/10 disabled:opacity-50"
                data-testid="tpl-delete"
              >
                <Trash2 size={12} />
                Delete
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              disabled={isPending}
              data-testid="tpl-save"
            >
              {isEdit ? 'Save changes' : 'Create template'}
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  hint?: string;
  children: React.ReactNode;
}

function Field({ label, hint, children }: FieldProps) {
  return (
    <div>
      <label className="block text-[12px] font-medium text-ink-200 mb-1">
        {label}
      </label>
      {children}
      {hint && <p className="text-[11.5px] text-ink-400 mt-1">{hint}</p>}
    </div>
  );
}

interface ScopeButtonProps {
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}

function ScopeButton({ active, onClick, disabled, children }: ScopeButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={
        'h-8 px-3 text-[12.5px] focus-ring transition-colors ' +
        (active
          ? 'bg-ink-700 text-ink-100'
          : 'bg-ink-850 text-ink-300 hover:text-ink-100')
      }
    >
      {children}
    </button>
  );
}

function AdminTemplatesListSkeleton() {
  return (
    <div className="h-full overflow-y-auto bg-ink-900" aria-busy="true">
      <header className="px-6 pt-5 pb-4 border-b border-ink-600 bg-ink-850">
        <Skeleton className="h-2.5 w-12 mb-1" />
        <Skeleton className="h-5 w-32 mb-1.5" />
        <Skeleton className="h-3 w-2/3 max-w-[420px]" />
      </header>
      <div className="p-6 max-w-3xl">
        <ul className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <li
              key={i}
              className="rounded-md border border-ink-600 bg-ink-850 px-4 py-3 flex items-start gap-3"
            >
              <div className="flex-1 min-w-0 space-y-2">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-3.5 w-48" inline />
                  <Skeleton className="h-5 w-12" inline />
                </div>
                <Skeleton className="h-3 w-3/4" />
              </div>
              <Skeleton className="h-8 w-16 shrink-0" inline />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
